/* Lunya collection adapter: enhance a real next-page link without replacing Symmetry filters. */
(() => {
  const sectionRequestUrl = (href, sectionId, baseUrl) => {
    const url = new URL(href, baseUrl);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const appendableCards = (grid) => [...grid.children].filter((el) => el.matches('product-block'));

  /*
    Each page render picks its own card ratio (main-collection.liquid takes the tallest or widest of
    THAT page's products under the "shortest"/"tallest" shapes), so an appended page can disagree with
    the grid it joins and break the row heights. Pin incoming cards to the ratio already on screen,
    as lunya-recently-viewed.js does for its fetched cards.
  */
  const CARD_RATIO = '.product-block__image .img-ar';
  const alignAspectRatio = (grid, cards) => {
    const source = grid.querySelector(CARD_RATIO);
    const ratio = source ? source.style.getPropertyValue('--aspect-ratio') : '';
    if (!ratio) return;
    cards.forEach((card) => card.querySelectorAll(CARD_RATIO).forEach((image) => {
      image.style.setProperty('--aspect-ratio', ratio);
    }));
  };

  const createRequestGate = () => {
    let generation = 0;
    let busy = false;
    let controller = null;
    return {
      isBusy: () => busy,
      invalidate: () => {
        generation += 1;
        controller?.abort();
        controller = null;
        busy = false;
      },
      run: async (work, commit, onError = () => {}) => {
        if (busy) return false;
        busy = true;
        const started = generation;
        controller = new AbortController();
        try {
          const result = await work(controller.signal);
          if (generation !== started) return false;
          commit(result);
          return true;
        } catch (error) {
          if (generation === started && error.name !== 'AbortError') onError(error);
          return false;
        } finally {
          if (generation === started) {
            busy = false;
            controller = null;
          }
        }
      }
    };
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { sectionRequestUrl, appendableCards, createRequestGate, alignAspectRatio };
  }
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!document.body.classList.contains('lunya-brand') || !document.body.classList.contains('template-collection')) return;
  if (window.__lunyaCollectionLoadMoreInstalled) return;
  window.__lunyaCollectionLoadMoreInstalled = true;

  const gridSelector = '.template-collection .collection-listing .product-grid';
  const linkSelector = '.template-collection .lunya-collection-load-more';
  const gate = createRequestGate();
  let filtering = false;

  const status = () => document.querySelector('.template-collection .lunya-collection-load-more-status');
  // Translated copy comes from snippets/lunya-collection-pagination.liquid; English is only the
  // fallback for markup rendered before those attributes existed.
  const label = (key, fallback) => status()?.dataset?.[key] || fallback;
  const updateCount = () => {
    const grid = document.querySelector(gridSelector);
    const output = status();
    if (grid && output) output.textContent = label('countLabel', 'Showing [count] products').replace('[count]', appendableCards(grid).length);
  };

  /*
    The pages this entry has appended, kept in history.state so Back/Forward, a reload and a
    non-bfcache return from a PDP can put them back: Symmetry's popstate handler re-fetches the URL,
    which is page 1, and the address bar never says more were loaded. Keyed by path and query, so a
    filter's pushState entry (or any other URL) never inherits another listing's pages.
  */
  const STATE_KEY = 'lunyaLoadMore';
  const entryKey = () => `${window.location.pathname}${window.location.search}`;
  const storedPages = () => {
    const stored = window.history?.state?.[STATE_KEY];
    return stored && stored.key === entryKey() && Array.isArray(stored.pages) ? stored.pages : [];
  };
  const rememberPage = (href) => {
    try {
      // Once per page: a partial restore leaves later pages listed, and the shopper may load them.
      const pages = [...storedPages().filter((stored) => !samePage(stored, href)), href];
      window.history.replaceState({ ...(window.history.state || {}), [STATE_KEY]: { key: entryKey(), pages } }, '');
    } catch (error) { /* history unavailable: the enhancement still works, it just isn't restored */ }
  };

  // Fetch one page and append it. `sourceUrl` must be the current link's href, so a restored page
  // can only ever continue the listing that is on screen.
  const loadPage = (link, sourceUrl, { onCommit = () => {}, onError, record = true } = {}) => {
    const grid = document.querySelector(gridSelector);
    return gate.run(async (signal) => {
      const response = await fetch(sectionRequestUrl(sourceUrl, link.dataset.sectionId, window.location.href), { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const incomingGrid = template.content.querySelector('.collection-listing .product-grid');
      const incomingRow = template.content.querySelector('.pagination-row');
      if (!incomingGrid || !incomingRow || appendableCards(incomingGrid).length === 0) {
        throw new Error('Incomplete collection response');
      }
      return { cards: appendableCards(incomingGrid), next: incomingRow.querySelector('.lunya-collection-load-more') };
    }, ({ cards, next }) => {
      if (!grid || !grid.isConnected || document.querySelector(gridSelector) !== grid || !link.isConnected) return;
      alignAspectRatio(grid, cards);
      grid.append(...cards);
      if (next) link.href = next.href;
      else link.remove();
      if (record) rememberPage(sourceUrl);
      updateCount();
      onCommit(cards);
    }, onError);
  };

  // The same listing page: path and page number. A re-rendered link can carry incidental params
  // (`section_id`, ordering) that the stored href did not.
  const samePage = (left, right) => {
    const a = new URL(left, window.location.href);
    const b = new URL(right, window.location.href);
    return a.pathname === b.pathname && a.searchParams.get('page') === b.searchParams.get('page');
  };

  let restoring = false;
  const restorePages = async () => {
    const pages = storedPages();
    if (restoring || pages.length === 0 || filtering) return;
    restoring = true;
    /*
      Replayed pages are not re-recorded: the record already lists them. A fetch that fails or is
      aborted (a filter change, popstate) leaves the record untouched, so a later reload or Back can
      try again — a failed replay is not evidence the pages are gone. Only a listing that answers
      and no longer offers the next stored page (fewer pages now, or a different one) shortens it.
    */
    let restored = 0;
    let stale = false;
    try {
      for (const href of pages) {
        if (filtering) break;
        const link = document.querySelector(linkSelector);
        if (!link || !samePage(link.href, href)) { stale = true; break; }
        // eslint-disable-next-line no-await-in-loop
        if (!(await loadPage(link, link.href, { record: false }))) break;
        restored += 1;
      }
    } finally {
      restoring = false;
    }
    // Same entry, same record as when we started: anything else means the shopper has moved on.
    const current = storedPages();
    if (stale && !filtering && current.length === pages.length && current.every((href, index) => href === pages[index])) {
      try {
        window.history.replaceState({ ...(window.history.state || {}), [STATE_KEY]: { key: entryKey(), pages: pages.slice(0, restored) } }, '');
      } catch (error) { /* the record simply stays as it was */ }
    }
  };

  document.addEventListener('collection:before-replace', () => {
    filtering = true;
    gate.invalidate();
  });
  document.addEventListener('collection:after-replace', () => {
    filtering = false;
    updateCount();
    restorePages();
  });
  document.addEventListener('collection:replace-failed', () => {
    // The URL already changed, but Symmetry left the old grid and link in place.
    // Keep that link blocked until a successful replacement brings them back in sync.
    document.querySelector(linkSelector)?.setAttribute('aria-disabled', 'true');
    const output = status();
    if (output) output.textContent = label('filterErrorLabel', 'Could not update products. Please try again.');
  });
  window.addEventListener('popstate', () => gate.invalidate());

  document.addEventListener('click', async (event) => {
    const link = event.target.closest(linkSelector);
    if (!link || !window.fetch || !window.AbortController || !window.URL) return;
    const grid = document.querySelector(gridSelector);
    const output = status();
    if (!grid || !output || !link.dataset.sectionId) return;
    event.preventDefault();
    if (filtering || restoring || gate.isBusy()) return;

    const originalLabel = link.textContent;
    link.setAttribute('aria-disabled', 'true');
    link.setAttribute('aria-busy', 'true');
    link.textContent = link.dataset.loadingLabel || 'Loading…';
    output.textContent = label('loadingLabel', 'Loading more products');
    await loadPage(link, link.href, {
      onCommit: (cards) => {
        const firstLink = cards[0].querySelector('.product-block__detail a.product-link[href], a.product-link[href]');
        if (firstLink) firstLink.focus();
      },
      onError: () => {
        output.textContent = label('errorLabel', 'Could not load more products. Try again.');
      }
    });

    if (link.isConnected) {
      link.removeAttribute('aria-disabled');
      link.removeAttribute('aria-busy');
      link.textContent = originalLabel;
    }
  });

  // A reload, or a return from a PDP that the browser could not restore from bfcache.
  restorePages();
})();
